import { useEffect } from 'react';
import { CheckCircle, X, XCircle, AlertTriangle, Info, Bell } from 'lucide-react';
import { useNotificationStore } from '@/stores';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';

export function NotificationDropdown() {
  const { notifications, removeNotification, clearRead, markAllAsRead } = useNotificationStore();

  const visibleNotifications = notifications;

  // Mark all success notifications as read when dropdown is opened
  useEffect(() => {
    if (visibleNotifications.length > 0) {
      markAllAsRead();
    }
  }, []);

  const formatTimestamp = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (visibleNotifications.length === 0) {
    return (
      <div className="w-96 p-6 text-center">
        <div className="text-gray-400 mb-2">
          <Bell className="w-12 h-12 mx-auto" />
        </div>
        <p className="text-sm text-gray-500">No notifications</p>
      </div>
    );
  }

  return (
    <div className="w-96">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-sm text-gray-900">Notifications</h3>
        {visibleNotifications.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearRead}
            className="text-xs h-7"
          >
            Clear Read
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <ScrollArea className="max-h-[400px]">
        <div className="p-2 space-y-2">
          {visibleNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`flex items-start gap-2 p-3 rounded-lg border transition-opacity ${
                notification.read ? 'bg-gray-50 border-gray-200' : notification.type === 'error' ? 'bg-red-50 border-red-200' : notification.type === 'warning' ? 'bg-amber-50 border-amber-200' : notification.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'
              }`}
            >
              <div className="flex-shrink-0 mt-0.5">
                {notification.type === 'error' ? <XCircle className="w-4 h-4 text-red-600" /> : notification.type === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-600" /> : notification.type === 'success' ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Info className="w-4 h-4 text-blue-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 text-xs">
                  {notification.title}
                </div>
                {notification.message && (
                  <div className="text-xs text-gray-700 mt-0.5">
                    {notification.message}
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-1">
                  {formatTimestamp(notification.timestamp)}
                </div>
                {notification.action && (
                  <button
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('navigate-page', { detail: { page: notification.action!.navigateTo, jobId: notification.action!.jobId } }));
                    }}
                    className="mt-1.5 text-xs px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    {notification.action.label}
                  </button>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0"
                aria-label={`Dismiss ${notification.title}`}
                onClick={() => removeNotification(notification.id)}
              >
                <X className="w-3 h-3 text-gray-500" />
              </Button>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
